import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $accountByIdStore, $currenciesStore, $initialAccountsStore, $profileFeatureStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { AccountType } from '@shared/api';
import { Managers, Sidebar } from './widgets';
import { AccountTileBig, Cards, List as Transactions, PageContainer, View } from '@widgets';

export const AccountPage = () => {
  const params = useParams<{ id: string }>();
  const { navigate } = useRouteHook();
  const id = useMemo(() => (params.id ? parseInt(params.id) : undefined), [params]);

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const accountStore = useUnit($initialAccountsStore.store);
  const accountDetailsStore = useUnit($accountByIdStore.store);

  const [selectedAccountId, setSelectedAccountId] = useState<number | undefined>(id);

  const { t } = useTranslation('account');

  useEffect(() => {
    $currenciesStore.events.getCurrencies();
  }, []);

  useEffect(() => {
    if (!profileFeatureStore.data?.accountFlows?.length) navigate(RoutePaths.dashboard.onNavigate());
    if (!selectedAccountId) return;
    $accountByIdStore.events.getAccount({ id: selectedAccountId });
    return () => $accountByIdStore.events.clear();
  }, [selectedAccountId]);

  useEffect(() => {
    if (accountDetailsStore.loading) return;
    if (accountDetailsStore.error !== 'Action is forbidden.') return;
    $accountByIdStore.events.clear();
    navigate(RoutePaths.error.onNavigate(), { replace: true });
  }, [accountDetailsStore.loading, accountDetailsStore.error]);

  const mainAccount = useMemo(() => {
    if (!accountDetailsStore.data || !accountStore.data) return undefined;
    const { mainAccId } = accountDetailsStore.data;
    return mainAccId === null ? accountDetailsStore.data : accountStore.data.find((a) => a.id === mainAccId);
  }, [accountDetailsStore.data, accountStore.data]);
  const subAccounts = useMemo(() => {
    if (!mainAccount) return accountStore.data.filter((a) => a.mainAccId != null);
    return accountStore.data.filter((a) => a.mainAccId === mainAccount.id);
  }, [mainAccount, accountStore.data]);
  const accountIds = useMemo(() => [selectedAccountId!], [selectedAccountId]);
  return (
    <PageContainer title={t('title')} back backText={t('backText')}>
      <View
        loading={accountDetailsStore.loading}
        error={accountDetailsStore.error}
        onRefresh={() => $accountByIdStore.events.getAccount({ id: selectedAccountId! })}
        noData={!accountDetailsStore.data}
      >
        {accountDetailsStore.data && (
          <main className={cn('flex w-full flex-row gap-6', 'max-[1440px]:flex-col')}>
            <div
              className={cn(
                'flex flex-col gap-6',
                accountDetailsStore.data.type === AccountType.Card && 'w-full',
                accountDetailsStore.data.type === AccountType.Payment && ['w-[calc(100%-18em)]', 'max-[1440px]:w-full'],
              )}
            >
              <AccountTileBig
                account={accountDetailsStore.data}
                mainAccount={mainAccount}
                subAccounts={subAccounts}
                selectedAccountId={selectedAccountId}
                onAccountSwitch={(accountId) => setSelectedAccountId(accountId)}
              />
              {profileFeatureStore.data?.userManagement?.viewUsers && <Managers accountId={selectedAccountId!} />}
              <Cards
                className={cn(
                  PaletteUtils.isVcfcPalette && 'rounded-xl bg-white dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
                )}
                title={t('card.title')}
                listType={false}
                accountId={selectedAccountId}
              />
              <Transactions accountType={accountDetailsStore.data.type} accountIds={accountIds} filter search />
            </div>
            <Sidebar />
          </main>
        )}
      </View>
    </PageContainer>
  );
};
